import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  ExternalLink,
  Globe2,
  Loader2,
  MapPin,
  Plus,
  Search,
  Sparkles,
  X,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { CommanServices } from '@/services/commanServices';
import {
  DEFAULT_HAZARD_IDS,
  HAZARD_FILTERS,
  suggestionPricing,
} from './externalEvents';

const numberOf = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && Number.isFinite(Number(value))) return Number(value);
  }
  return 0;
};

const normalizedIds = (value) => (
  Array.isArray(value)
    ? value.map((item) => String(item?.id || item || '').trim().toLowerCase()).filter(Boolean)
    : []
);

export const connectionCoversHazard = (connection, hazard) => {
  if (!connection || connection.is_active === false || !hazard?.id) return false;
  const hazardId = String(hazard.id).toLowerCase();
  const coverage = String(connection.hazard_coverage || '').toLowerCase();
  if (coverage === 'all') return true;

  const authoritativeIds = normalizedIds(
    Array.isArray(connection.effective_hazard_ids)
      ? connection.effective_hazard_ids
      : Array.isArray(connection.hazard_ids)
        ? connection.hazard_ids
        : connection.hazards
  );
  if (coverage === 'selected') return authoritativeIds.includes(hazardId);
  if (authoritativeIds.length > 0) return authoritativeIds.includes(hazardId);

  const text = [
    connection.name,
    connection.base_url,
    connection.description,
    connection.origin_key,
  ].filter(Boolean).join(' ').toLowerCase();
  return (hazard.feedHints || []).some((hint) => text.includes(String(hint).toLowerCase()));
};

const providerEntry = (catalog, key) => {
  if (Array.isArray(catalog)) {
    return catalog.find((provider) => (
      String(provider?.key || provider?.id || '') === key ||
      normalizedIds(provider?.aliases).includes(String(key).toLowerCase())
    )) || {};
  }
  return catalog?.[key] || {};
};

const normalizedUrl = (value) => String(value || '').trim().replace(/\/+$/, '').toLowerCase();

const connectionMatchesProvider = (connection, key, template, metadata = {}) => {
  const providerKeys = new Set([
    key,
    metadata?.key,
    metadata?.origin_key,
    template?.origin_key,
    ...(Array.isArray(metadata?.aliases) ? metadata.aliases : []),
  ].map((value) => String(value || '').trim().toLowerCase()).filter(Boolean));
  const connectionKeys = [
    connection?.provider_key,
    connection?.origin_key,
  ].map((value) => String(value || '').trim().toLowerCase()).filter(Boolean);
  const names = [metadata?.name, template?.name]
    .map((value) => String(value || '').trim().toLowerCase())
    .filter(Boolean);
  const urls = [metadata?.base_url, template?.base_url]
    .map(normalizedUrl)
    .filter(Boolean);
  return (
    connectionKeys.some((value) => providerKeys.has(value)) ||
    names.includes(String(connection?.name || '').trim().toLowerCase()) ||
    urls.includes(normalizedUrl(connection?.base_url))
  );
};

const scopeOf = (value) => (
  ['worldwide', 'global'].includes(String(value || '').toLowerCase())
    ? 'worldwide'
    : 'operations_area'
);

const hazardCount = (hazard, items, preferItemCounts = false) => {
  const matchingItems = (items || []).filter((item) =>
    normalizedIds(item?.hazards).includes(String(hazard.id).toLowerCase())
  ).length;
  if (preferItemCounts) return matchingItems;
  const catalogCount = numberOf(hazard?.signal_counts?.total, hazard?.case_count, hazard?.count);
  if (catalogCount > 0) return catalogCount;
  return matchingItems;
};

const HazardDictionaryManager = ({
  hazardCatalog = [],
  items = [],
  preferItemCounts = false,
  initialHazardId = '',
  visibleHazardIds = DEFAULT_HAZARD_IDS,
  onVisibleHazardIdsChange,
  onFilter,
  onClose,
  onRefresh,
  onAddConnection,
}) => {
  const [selectedId, setSelectedId] = useState(initialHazardId || '');
  const [query, setQuery] = useState('');
  const [connections, setConnections] = useState([]);
  const [connectionsLoaded, setConnectionsLoaded] = useState(false);
  const [providerCatalog, setProviderCatalog] = useState([]);
  const [providerCatalogLoaded, setProviderCatalogLoaded] = useState(false);
  const [area, setArea] = useState(null);
  const [scope, setScope] = useState('operations_area');
  const [scopeSaving, setScopeSaving] = useState(false);
  const [connecting, setConnecting] = useState('');
  const [providerResults, setProviderResults] = useState({});
  const [probes, setProbes] = useState({});
  const [aiState, setAiState] = useState({ loading: false, error: '', items: null });

  const catalogById = useMemo(() => new Map(
    (hazardCatalog || []).map((hazard) => [String(hazard?.id || '').toLowerCase(), hazard])
  ), [hazardCatalog]);

  const library = useMemo(() => HAZARD_FILTERS.map((definition) => ({
    ...definition,
    ...(catalogById.get(definition.id) || {}),
    id: definition.id,
    icon: definition.icon,
    suggestions: definition.suggestions,
    feedHints: definition.feedHints,
    keywords: definition.keywords,
    liveCount: hazardCount(
      { ...definition, ...(catalogById.get(definition.id) || {}) },
      items,
      preferItemCounts
    ),
  })), [catalogById, items, preferItemCounts]);

  const selected = library.find((hazard) => hazard.id === selectedId) || null;
  const visibleHazards = library.filter((hazard) => {
    const needle = query.trim().toLowerCase();
    return !needle || `${hazard.label} ${hazard.id}`.toLowerCase().includes(needle);
  });

  const loadConnections = useCallback(async () => {
    const [connectionsResult, catalogResult] = await Promise.allSettled([
        CommanServices.getApiConnections(),
        CommanServices.getSignalCatalog?.(),
    ]);
    try {
      const connectionsResponse = connectionsResult.status === 'fulfilled'
        ? connectionsResult.value
        : null;
      const catalogResponse = catalogResult.status === 'fulfilled'
        ? catalogResult.value
        : null;
      setConnections(
        connectionsResponse?.status && Array.isArray(connectionsResponse.data)
          ? connectionsResponse.data
          : []
      );
      const signalData = catalogResponse?.status ? catalogResponse.data || {} : {};
      setProviderCatalog(signalData.providers || signalData.provider_catalog || []);
    } catch (error) {
      setConnections([]);
    } finally {
      setConnectionsLoaded(true);
      setProviderCatalogLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  useEffect(() => {
    if (!selected) return undefined;
    let active = true;
    setAiState({ loading: false, error: '', items: null });
    const loadContext = async () => {
      const [scopesResponse, areaResponse, probesResponse] = await Promise.allSettled([
        CommanServices.getHazardScopes(),
        CommanServices.getOperationsArea(),
        CommanServices.probeSuggestionUrls((selected.suggestions || []).map((suggestion) => suggestion.url)),
      ]);
      if (!active) return;
      const scopes = scopesResponse.status === 'fulfilled' && scopesResponse.value?.status
        ? scopesResponse.value.data?.scopes || {}
        : {};
      setScope(scopeOf(scopes?.[selected.id]?.scope || selected.watch_scope));
      setArea(
        areaResponse.status === 'fulfilled' && areaResponse.value?.status
          ? areaResponse.value.data || null
          : null
      );
      setProbes(
        probesResponse.status === 'fulfilled' && probesResponse.value?.status
          ? probesResponse.value.data?.results || {}
          : {}
      );
    };
    loadContext();
    return () => { active = false; };
  }, [selected]);

  const coveringConnections = selected
    ? connections.filter((connection) => connectionCoversHazard(connection, selected))
    : [];
  const reportedCoverageCount = numberOf(selected?.coverage?.source_count);
  const effectiveCoverageCount = Math.max(coveringConnections.length, reportedCoverageCount);
  const hasCoverage = effectiveCoverageCount > 0;
  const coverageSourceNames = [...new Set([
    ...coveringConnections.map((connection) => connection?.name),
    ...(Array.isArray(selected?.coverage?.sources)
      ? selected.coverage.sources.map((source) => source?.name)
      : []),
  ].filter(Boolean))];

  const manageHazard = (hazard) => {
    setSelectedId(hazard.id);
  };

  const toggleVisibility = (hazardId) => {
    const next = visibleHazardIds.includes(hazardId)
      ? visibleHazardIds.filter((id) => id !== hazardId)
      : [...visibleHazardIds, hazardId];
    onVisibleHazardIdsChange?.(next);
  };

  const viewCases = () => {
    if (!selected) return;
    if (!visibleHazardIds.includes(selected.id)) {
      onVisibleHazardIdsChange?.([...visibleHazardIds, selected.id]);
    }
    onFilter?.(selected.id);
    onClose?.();
  };

  const saveScope = async (nextScope) => {
    if (!selected || scopeSaving) return;
    setScopeSaving(true);
    try {
      const response = await CommanServices.setHazardScope({
        hazard_id: selected.id,
        scope: nextScope,
        keywords: selected.keywords || [],
      });
      if (!response?.status) {
        toast.error(response?.message || 'Could not update the watch scope.');
        return;
      }
      setScope(nextScope);
      await onRefresh?.();
      toast.success(`${selected.label} now watches ${nextScope === 'worldwide' ? 'worldwide' : 'the Operations Area'}.`);
    } catch (error) {
      toast.error('Could not update the watch scope.');
    } finally {
      setScopeSaving(false);
    }
  };

  const catalogProvidersForHazard = selected && Array.isArray(providerCatalog)
    ? providerCatalog.filter((provider) =>
        provider?.one_click === true &&
        normalizedIds(provider?.effective_hazard_ids || provider?.hazard_ids).includes(selected.id)
      )
    : [];
  const authoritativeProviderKeys = normalizedIds(selected?.suggested_provider_keys);
  const authoritativeCanonicalKeys = authoritativeProviderKeys
    .map((key) => key === 'eonet' ? 'nasa_eonet' : key);
  const providerKeys = authoritativeCanonicalKeys.length > 0
    ? authoritativeCanonicalKeys
    : catalogProvidersForHazard.map((provider) => String(provider.key));
  const providers = [...new Set(providerKeys)].map((key) => {
    const metadata = providerEntry(providerCatalog, key);
    const template = metadata?.name && metadata?.base_url
      ? {
          name: metadata.name,
          category: metadata.category || 'monitoring_service',
          origin_key: metadata.origin_key || metadata.key,
          access_method: metadata.access_method,
          base_url: metadata.base_url,
          description: metadata.description || '',
          auth_type: 'none',
          purposes: [],
          is_active: true,
        }
      : null;
    const existingConnection = connections.find((connection) =>
      connectionMatchesProvider(connection, String(metadata?.key || key), template, metadata)
    );
    return {
      key: String(metadata?.key || key),
      template,
      metadata,
      serverConnect: metadata?.one_click === true && Boolean(metadata?.connect_endpoint),
      metadataPending: authoritativeCanonicalKeys.includes(String(key).toLowerCase()) &&
        !providerCatalogLoaded,
      existingConnection,
    };
  }).filter((provider) => provider.template && provider.serverConnect);

  const connectProvider = async (provider) => {
    if (!selected || connecting || provider.metadataPending) return;
    setConnecting(provider.key);
    try {
      let response;
      if (provider.serverConnect && typeof CommanServices.connectForesightProvider === 'function') {
        // Provider connections may cover several hazards, so Hazard Manager
        // never mutates their shared geography. The canonical per-hazard scope
        // above controls this hazard's reach; dedicated source geography is
        // managed explicitly in API Connections.
        response = await CommanServices.connectForesightProvider(provider.key, {
          hazard_id: selected.id,
        });
      } else {
        throw new Error('The provider is not available for one-click connection.');
      }
      if (!response?.status) {
        const detail = response?.message || 'Connection failed.';
        setProviderResults((current) => ({ ...current, [provider.key]: { ok: false, detail } }));
        toast.error(detail);
        return;
      }
      const result = response.data || {};
      const connection = result.connection || result;
      let detail = result.already_connected
        ? 'Already connected · coverage updated.'
        : result.updated
          ? 'Connection coverage updated.'
          : 'Connected.';
      let verified = null;
      if (connection?.id && typeof CommanServices.testApiConnection === 'function') {
        const testResponse = await CommanServices.testApiConnection(connection.id);
        verified = Boolean(testResponse?.status && testResponse?.data?.ok);
        detail = verified
          ? `${result.already_connected ? 'Already connected' : 'Connected'} · verified${
              testResponse.data?.status_code ? ` (HTTP ${testResponse.data.status_code})` : ''
            }.`
          : `${detail.replace(/\.$/, '')} · verification failed${
              testResponse?.data?.error ? `: ${testResponse.data.error}` : '.'
            }`;
      }
      setProviderResults((current) => ({
        ...current,
        [provider.key]: { ok: verified !== false, detail },
      }));
      await loadConnections();
      await onRefresh?.();
      if (verified === false) {
        toast.error(`${provider.metadata?.name || provider.template.name}: ${detail}`);
      } else {
        toast.success(`${provider.metadata?.name || provider.template.name}: ${detail}`);
      }
    } catch (error) {
      const detail = 'Provider connection is temporarily unavailable.';
      setProviderResults((current) => ({
        ...current, [provider.key]: { ok: false, detail },
      }));
      toast.error(`${provider.metadata?.name || provider.template.name}: ${detail}`);
    } finally {
      setConnecting('');
    }
  };

  const discover = async () => {
    if (!selected) return;
    setAiState({ loading: true, error: '', items: null });
    try {
      const response = await CommanServices.discoverApisForHazard(selected.label);
      if (response?.status) {
        setAiState({ loading: false, error: '', items: response.data?.suggestions || [] });
      } else {
        setAiState({ loading: false, error: response?.message || 'AI discovery is unavailable.', items: null });
      }
    } catch (error) {
      setAiState({ loading: false, error: 'AI discovery is unavailable.', items: null });
    }
  };

  const areaLabel = area?.country_name || area?.country_iso2 || 'Not configured';
  const radius = Number(area?.radius_km);

  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-start justify-center overflow-y-auto bg-slate-950/60 p-3 backdrop-blur-[2px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      data-testid="hazard-dictionary-modal"
    >
      <div className="my-auto flex max-h-[calc(100vh-1.5rem)] w-[840px] max-w-full flex-col overflow-hidden rounded-2xl bg-slate-50 shadow-2xl ring-1 ring-white/20">
        <header className="flex flex-shrink-0 items-center justify-between gap-3 bg-slate-950 px-4 py-3 text-white">
          <div className="flex min-w-0 items-center gap-2.5">
            {selected ? (
              <button
                type="button"
                onClick={() => setSelectedId('')}
                className="rounded-lg p-1.5 text-slate-300 hover:bg-white/10 hover:text-white"
                data-testid="hazard-manager-back"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            ) : (
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-400/15 text-teal-300">
                <BookOpen className="h-4 w-4" />
              </span>
            )}
            <div className="min-w-0">
              <h3 className="truncate text-sm font-bold">
                {selected ? `${selected.icon} ${selected.label}` : 'Hazard Dictionary'}
              </h3>
              <p className="truncate text-[10px] text-slate-300">
                {selected ? 'Coverage, watch scope and source connections' : '24 hazards · live cases and source coverage'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-white/10 hover:text-white"
            data-testid="hazard-dictionary-close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {!selected ? (
          <div className="flex min-h-0 flex-1 flex-col p-3">
            <div className="mb-2 flex items-center gap-2">
              <label className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-lg bg-white px-3 ring-1 ring-slate-200 focus-within:ring-teal-500">
                <Search className="h-3.5 w-3.5 text-slate-400" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Find a hazard"
                  className="min-w-0 flex-1 border-0 bg-transparent text-xs text-slate-900 outline-none"
                  data-testid="hazard-dictionary-search"
                />
              </label>
              <button
                type="button"
                onClick={() => { onFilter?.('all'); onClose?.(); }}
                className="h-9 rounded-lg bg-slate-900 px-3 text-[10px] font-bold text-white"
                data-testid="hazard-dictionary-all"
              >
                All hazards ({items.length})
              </button>
            </div>
            <div className="grid min-h-0 flex-1 grid-cols-2 gap-1.5 overflow-y-auto pr-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:grid-cols-3 lg:grid-cols-4">
              {visibleHazards.map((hazard) => (
                <div
                  key={hazard.id}
                  className={`flex min-w-0 items-center gap-1 rounded-lg bg-white p-1 ring-1 transition ${
                    visibleHazardIds.includes(hazard.id)
                      ? 'ring-teal-300'
                      : 'ring-slate-200'
                  }`}
                  data-testid={`hazard-dictionary-row-${hazard.id}`}
                >
                  <button
                    type="button"
                    onClick={() => toggleVisibility(hazard.id)}
                    aria-pressed={visibleHazardIds.includes(hazard.id)}
                    className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-1.5 py-1 text-left hover:bg-teal-50"
                    data-testid={`hazard-dictionary-toggle-${hazard.id}`}
                    title={`${visibleHazardIds.includes(hazard.id) ? 'Hide' : 'Show'} ${hazard.label} in ForeSight filters`}
                  >
                    <span aria-hidden="true">{hazard.icon}</span>
                    <span className="min-w-0 flex-1 truncate text-[11px] font-semibold text-slate-800">{hazard.label}</span>
                    <span className={`inline-grid min-w-[22px] place-items-center rounded-full px-1.5 py-0.5 text-[9px] font-bold ${
                      hazard.liveCount > 0 ? 'bg-teal-100 text-teal-800' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {hazard.liveCount}
                    </span>
                    <span className={`inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                      visibleHazardIds.includes(hazard.id)
                        ? 'border-teal-700 bg-teal-700'
                        : 'border-slate-300 bg-white'
                    }`}>
                      {visibleHazardIds.includes(hazard.id) && <CheckCircle2 className="h-3 w-3 text-white" />}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => manageHazard(hazard)}
                    className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-teal-700"
                    data-testid={`hazard-dictionary-item-${hazard.id}`}
                    aria-label={`Manage ${hazard.label} coverage and sources`}
                    title="Manage coverage, scope and sources"
                  >
                    <ArrowLeft className="h-3.5 w-3.5 rotate-180" />
                  </button>
                </div>
              ))}
            </div>
            <footer className="mt-3 flex items-center justify-between gap-3 border-t border-slate-200 pt-3 text-[10px] text-slate-500">
              <b data-testid="hazard-dictionary-visible-count">
                {visibleHazardIds.length} of {library.length} types visible
              </b>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="font-bold text-teal-700 hover:text-teal-900"
                  onClick={() => onVisibleHazardIdsChange?.(library.map((hazard) => hazard.id))}
                  data-testid="hazard-dictionary-select-all"
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="font-bold text-slate-600 hover:text-slate-900"
                  onClick={() => onVisibleHazardIdsChange?.(DEFAULT_HAZARD_IDS)}
                  data-testid="hazard-dictionary-reset"
                >
                  Reset to default
                </button>
              </div>
            </footer>
          </div>
        ) : (
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-y-auto p-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:grid-cols-[0.94fr_1.06fr]">
            <div className="space-y-2.5">
              <section className={`rounded-xl p-3 ring-1 ${
                selected.liveCount > 0
                  ? 'bg-teal-50 text-teal-900 ring-teal-200'
                  : 'bg-white text-slate-800 ring-slate-200'
              }`} data-testid="hazard-manager-event-status">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.14em] opacity-70">Current status</p>
                    <b className="mt-1 block text-sm">
                      {selected.liveCount > 0
                        ? `${selected.liveCount} active ${selected.liveCount === 1 ? 'case' : 'cases'}`
                        : 'No events detected'}
                    </b>
                  </div>
                  {selected.liveCount > 0
                    ? <CheckCircle2 className="h-5 w-5 text-teal-700" />
                    : <Zap className="h-5 w-5 text-slate-400" />}
                </div>
              </section>

              <section className={`rounded-xl p-3 ring-1 ${
                hasCoverage ? 'bg-emerald-50 ring-emerald-200' : 'bg-amber-50 ring-amber-200'
              }`} data-testid={hasCoverage ? 'hazard-manager-covered' : 'hazard-manager-coverage-gap'}>
                <div className="flex items-start gap-2">
                  {hasCoverage
                    ? <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-700" />
                    : <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-700" />}
                  <div>
                    <b className={`text-xs ${hasCoverage ? 'text-emerald-900' : 'text-amber-900'}`}>
                      {hasCoverage ? `${effectiveCoverageCount} connected source${effectiveCoverageCount === 1 ? '' : 's'}` : 'Coverage gap'}
                    </b>
                    <p className={`mt-1 text-[10px] leading-relaxed ${hasCoverage ? 'text-emerald-800' : 'text-amber-800'}`}>
                      {coverageSourceNames.length > 0
                        ? coverageSourceNames.slice(0, 4).join(' · ')
                        : hasCoverage
                          ? 'Authoritative ForeSight coverage is active for this hazard.'
                        : connectionsLoaded
                          ? 'No enabled connection currently covers this hazard.'
                          : 'Checking connected sources…'}
                    </p>
                  </div>
                </div>
              </section>

              <section className="rounded-xl bg-white p-3 ring-1 ring-slate-200" data-testid="hazard-manager-watch-scope">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.14em] text-slate-500">Per-hazard watch scope</p>
                    <p className="mt-0.5 text-[10px] text-slate-500">Choose how far ForeSight watches {selected.label}.</p>
                  </div>
                  {scopeSaving && <Loader2 className="h-3.5 w-3.5 animate-spin text-teal-700" />}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-1.5">
                  {[
                    {
                      value: 'operations_area',
                      label: `Operations Area${Number.isFinite(radius) ? ` · ${radius.toLocaleString()} km` : ''}`,
                      icon: MapPin,
                    },
                    { value: 'worldwide', label: 'Worldwide', icon: Globe2 },
                  ].map((option) => {
                    const ScopeIcon = option.icon;
                    const active = scope === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        disabled={scopeSaving}
                        onClick={() => saveScope(option.value)}
                        aria-pressed={active}
                        data-testid={`hazard-manager-scope-${option.value}`}
                        className={`flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-[10px] font-bold ${
                          active
                            ? 'bg-slate-950 text-white ring-2 ring-teal-400'
                            : 'bg-slate-50 text-slate-600 ring-1 ring-slate-200'
                        }`}
                      >
                        <ScopeIcon className="h-3.5 w-3.5" />
                        {option.label}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-2 rounded-lg bg-slate-50 px-2.5 py-2" data-testid="hazard-manager-operations-area">
                  <div className="flex items-center justify-between gap-2">
                    <span>
                      <small className="block text-[9px] font-bold uppercase text-slate-400">Institution Operations Area · read only</small>
                      <b className="text-[11px] text-slate-800">{areaLabel}</b>
                    </span>
                    <b className="text-[10px] text-teal-700">
                      {Number.isFinite(radius) ? `${radius.toLocaleString()} km radius` : 'Radius unavailable'}
                    </b>
                  </div>
                </div>
                <p className="mt-2 text-[9px] leading-relaxed text-slate-500">
                  Source geographic coverage and this hazard’s watch scope both define effective reach. Operational risk is still evaluated against the Operations Area.
                </p>
              </section>
            </div>

            <div className="space-y-2.5">
              <section className="rounded-xl bg-white p-3 ring-1 ring-slate-200" data-testid="hazard-manager-superconnect">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.14em] text-teal-700">
                      ⚡ SuperConnect · one click
                    </p>
                    <p className="mt-0.5 text-[10px] text-slate-500">
                      Connected and verified without changing shared source geography.
                    </p>
                  </div>
                  <Zap className="h-4 w-4 text-teal-700" />
                </div>
                {providers.length === 0 ? (
                  <p className="mt-2 rounded-lg bg-slate-50 px-2.5 py-2 text-[10px] text-slate-500">
                    {providerCatalogLoaded
                      ? 'No verified one-click provider is curated for this hazard yet.'
                      : 'Loading verified providers…'}
                  </p>
                ) : (
                  <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                    {providers.map((provider) => {
                      const connected = Boolean(provider.existingConnection);
                      const coversSelected = connected &&
                        connectionCoversHazard(provider.existingConnection, selected);
                      const result = providerResults[provider.key];
                      const metadata = provider.metadata || {};
                      return (
                        <div key={provider.key} className="rounded-lg bg-slate-50 p-2 ring-1 ring-slate-200">
                          <div className="flex items-start justify-between gap-2">
                            <span className="min-w-0">
                              <b className="block truncate text-[10px] text-slate-800">{metadata.name || provider.template.name}</b>
                              <small className="mt-0.5 block truncate text-[9px] text-slate-500">
                                {metadata.status || metadata.access_method || provider.template.access_method || 'Curated provider'}
                              </small>
                            </span>
                            <button
                              type="button"
                              disabled={Boolean(connecting) || coversSelected || provider.metadataPending}
                              onClick={() => connectProvider(provider)}
                              className={`flex-shrink-0 rounded-md px-2 py-1 text-[9px] font-bold ${
                                coversSelected
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : 'bg-slate-950 text-white hover:bg-teal-800'
                              }`}
                              data-testid={`hazard-provider-connect-${provider.key}`}
                            >
                              {connecting === provider.key
                                ? 'Connecting…'
                                : provider.metadataPending
                                  ? 'Loading…'
                                : coversSelected
                                  ? 'Connected'
                                  : connected
                                    ? 'Add coverage'
                                    : 'Connect'}
                            </button>
                          </div>
                          {result && (
                            <p className={`mt-1 text-[9px] ${result.ok ? 'text-emerald-700' : 'text-amber-700'}`}>
                              {result.detail}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              <section className="rounded-xl bg-white p-3 ring-1 ring-slate-200" data-testid="hazard-manager-suggestions">
                <p className="text-[9px] font-black uppercase tracking-[0.14em] text-slate-500">Curated APIs · live status</p>
                <div className="mt-2 space-y-1.5">
                  {(selected.suggestions || []).slice(0, 4).map((suggestion) => {
                    const pricing = suggestionPricing(suggestion);
                    const probe = probes[suggestion.url];
                    return (
                      <div key={suggestion.url} className="flex min-w-0 items-center gap-1.5">
                        <a
                          href={suggestion.url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex min-w-0 flex-1 items-center gap-1 truncate text-[10px] font-semibold text-teal-700 hover:underline"
                        >
                          <ExternalLink className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{suggestion.name}</span>
                        </a>
                        <span className={`rounded-full border px-1.5 py-0.5 text-[8px] font-bold ${pricing.cls}`}>{pricing.label}</span>
                        {probe && (
                          <span className={`rounded-full px-1.5 py-0.5 text-[8px] font-bold ${
                            probe.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                          }`}>
                            {probe.ok ? 'Online' : 'Unavailable'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>

              <section className="rounded-xl bg-white p-3 ring-1 ring-slate-200" data-testid="hazard-manager-ai">
                <div className="flex items-center justify-between gap-2">
                  <span>
                    <p className="text-[9px] font-black uppercase tracking-[0.14em] text-slate-500">AI source discovery</p>
                    <p className="mt-0.5 text-[9px] text-slate-400">On demand · suggestions require operator verification.</p>
                  </span>
                  {aiState.items === null && !aiState.loading && (
                    <button
                      type="button"
                      onClick={discover}
                      className="inline-flex items-center gap-1 rounded-md bg-teal-50 px-2 py-1.5 text-[9px] font-bold text-teal-800 ring-1 ring-teal-200"
                      data-testid="hazard-manager-ai-discover"
                    >
                      <Sparkles className="h-3 w-3" /> Discover
                    </button>
                  )}
                </div>
                {aiState.loading && <p className="mt-2 text-[9px] text-teal-700">Searching available sources…</p>}
                {aiState.error && <p className="mt-2 text-[9px] text-amber-700">{aiState.error}</p>}
                {Array.isArray(aiState.items) && (
                  <div className="mt-2 space-y-1">
                    {aiState.items.length === 0 && <p className="text-[9px] text-slate-400">No additional source found.</p>}
                    {aiState.items.slice(0, 3).map((item) => (
                      <a key={item.url || item.name} href={item.url} target="_blank" rel="noreferrer" className="block truncate text-[9px] font-semibold text-teal-700 hover:underline">
                        {item.name}
                      </a>
                    ))}
                  </div>
                )}
              </section>

              <div className="grid grid-cols-2 gap-1.5">
                <button
                  type="button"
                  onClick={viewCases}
                  className="flex items-center justify-center gap-1.5 rounded-lg bg-teal-50 py-2 text-[10px] font-bold text-teal-900 ring-1 ring-teal-200 hover:bg-teal-100"
                  data-testid="hazard-manager-view-cases"
                >
                  <Search className="h-3.5 w-3.5" />
                  View matching cases
                </button>
                <button
                  type="button"
                  onClick={() => onAddConnection?.(selected, scope)}
                  className="flex items-center justify-center gap-1.5 rounded-lg bg-slate-950 py-2 text-[10px] font-bold text-white hover:bg-teal-900"
                  data-testid="hazard-manager-add-connection"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add API connection · prefilled
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default HazardDictionaryManager;
